import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Steps } from 'primereact/steps';
import { Toast } from 'primereact/toast';

import { HASACCESS } from '../../../shared/utility';
import { ZATCA_UNIT_SUBMENU_ID } from '../../../shared/Config';
import {
    ZATCA_STATUS,
    invoiceTypeLabel,
    formatDate,
    formatDateTime,
    apiErrorMessage
} from './zatcaShared';
import {
    getUnitOnboardingStatus,
    requestComplianceCertificate,
    runComplianceCheck,
    requestProductionCertificate
} from './zatcaApi';

const STEP_INDEX = { 'request-compliance-csid': 0, 'run-compliance-checks': 1, 'ready': 2 };

const sectionStyle = { border: '1px solid #dee2e6', borderRadius: '4px', padding: '1em', marginTop: '1em' };
const warnStyle = { padding: '0.75em 1em', borderRadius: '4px', background: '#fff4e5', border: '1px solid #f5c26b', color: '#6b4500', marginTop: '0.75em' };
const okStyle = { padding: '0.75em 1em', borderRadius: '4px', background: '#e8f5e9', border: '1px solid #81c784', color: '#1b5e20', marginTop: '0.75em' };

/**
 * Connects one branch unit to ZATCA, in ZATCA's fixed order:
 *
 *   1. compliance certificate  — from a one-time password generated in the FATOORA portal
 *   2. compliance checks       — one per document type the unit issues
 *   3. production certificate  — after the checks pass; the unit can then issue invoices
 *
 * Every call goes to the ZATCA service (ZatcaTest), which records each step on
 * the unit and in its onboarding log. Nothing secret reaches this page: the OTP
 * is sent once and never kept, and the unit's key and certificate secret stay on
 * the server.
 */
class ZatcaOnboarding extends Component {
    state = {
        access: undefined,
        busy: false,
        status: null,
        otp: '',
        lastResults: {}
    }

    componentDidMount() {
        this.setState({ access: HASACCESS(ZATCA_UNIT_SUBMENU_ID) });
        this.loadStatus();
    }

    unitId = () => this.props.match.params.id

    userName = () => (this.props.loggedUserDetail && this.props.loggedUserDetail.userName) || null

    showError = (err, fallback) => {
        this.growl.show({ severity: 'error', summary: null, detail: apiErrorMessage(err, fallback), life: 8000 });
    }

    loadStatus = () => {
        this.setState({ busy: true });
        getUnitOnboardingStatus(this.unitId())
            .then(response => this.setState({ busy: false, status: response.data || null, lastResults: {} }))
            .catch(err => {
                this.setState({ busy: false });
                this.showError(err, 'Could not load the onboarding status. Is the ZATCA service running?');
            });
    }

    requestComplianceCertificate = () => {
        const otp = this.state.otp.trim();
        if (otp === '') {
            this.growl.show({ severity: 'error', summary: null, detail: 'Enter the OTP generated for this unit in the FATOORA portal' });
            return;
        }

        this.setState({ busy: true });
        requestComplianceCertificate(this.unitId(), otp, this.userName())
            .then(() => {
                this.setState({ busy: false, otp: '' });
                this.growl.show({ severity: 'success', summary: null, detail: 'Compliance certificate issued. Run the compliance checks next.' });
                this.loadStatus();
            })
            .catch(err => {
                // A used or expired OTP is the usual cause; clear it so a fresh one is entered.
                this.setState({ busy: false, otp: '' });
                this.showError(err, 'ZATCA did not issue the compliance certificate');
            });
    }

    runComplianceCheck = (kind) => {
        this.setState({ busy: true });
        runComplianceCheck(this.unitId(), kind, this.userName())
            .then(response => {
                const result = response.data || {};
                this.growl.show({
                    severity: result.passed ? 'success' : 'warn',
                    summary: null,
                    detail: result.passed ? kind + ' check passed' : kind + ' check failed — see ZATCA\'s errors below'
                });
                this.loadStatus();
                this.setState(state => ({ lastResults: { ...state.lastResults, [kind]: result } }));
            })
            .catch(err => {
                this.setState({ busy: false });
                this.showError(err, 'The ' + kind.toLowerCase() + ' compliance check could not be run');
            });
    }

    requestProductionCertificate = () => {
        this.setState({ busy: true });
        requestProductionCertificate(this.unitId(), this.userName())
            .then(response => {
                const result = response.data || {};
                this.setState({ busy: false });
                this.growl.show({ severity: 'success', summary: null, detail: result.nextAction || 'Production certificate issued', life: 8000 });
                this.loadStatus();
            })
            .catch(err => {
                this.setState({ busy: false });
                this.showError(err, 'ZATCA did not issue the production certificate');
            });
    }

    /** The newest check for a document type: this session's result if newer, else the one on record. */
    latestCheck = (kind) => {
        const fresh = this.state.lastResults[kind];
        if (fresh) {
            return {
                kind,
                outcome: fresh.passed ? 'Passed' : 'Failed',
                zatcaStatus: fresh.reportingStatus || fresh.clearanceStatus,
                invoiceNumber: fresh.invoiceNumber,
                errors: fresh.errors || [],
                warnings: fresh.warnings || [],
                performedAt: new Date()
            };
        }
        const checks = (this.state.status && this.state.status.checks) || [];
        const recorded = checks.find(check => check.kind === kind);
        return recorded ? { ...recorded, errors: recorded.errors || [], warnings: recorded.warnings || [] } : null;
    }

    renderCredential = (title, credential) => (
        <div className="p-col-12 p-md-6">
            <h4 style={{ marginTop: 0 }}>{title}</h4>
            {!credential ? <p>Not issued yet.</p> :
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr><td style={{ width: '40%' }}>Issued</td><td>{formatDateTime(credential.issuedAt)}</td></tr>
                        <tr><td>Valid until</td><td>{formatDate(credential.validUntil)}{credential.expired ? ' (expired)' : ''}</td></tr>
                        <tr><td>Subject</td><td style={{ wordBreak: 'break-all' }}>{credential.certificateSubject}</td></tr>
                        <tr><td>Matches this unit's key</td><td>{credential.matchesPrivateKey ? 'Yes' : 'No'}</td></tr>
                    </tbody>
                </table>
            }
        </div>
    )

    renderCheck = (kind, canAct) => {
        const check = this.latestCheck(kind);
        const passed = check && check.outcome === 'Passed';
        const label = kind === 'Standard' ? 'Standard invoice (B2B)' : 'Simplified invoice (B2C)';
        const outcomeClass = passed ? 'p-button-success' : 'p-button-danger';

        return (
            <div key={kind} style={sectionStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5em' }}>
                    <strong>{label}</strong>
                    <span>
                        {check && <Button type="button" label={check.outcome} className={outcomeClass + ' p-button-rounded'} />}
                        {canAct &&
                            <Button type="button" label={check ? 'Run again' : 'Run check'} icon="pi pi-play"
                                className="ml1" onClick={() => this.runComplianceCheck(kind)} />
                        }
                    </span>
                </div>
                {!check && <p style={{ marginBottom: 0 }}>Not run yet.</p>}
                {check &&
                    <div style={{ marginTop: '0.5em' }}>
                        <div>ZATCA status: {check.zatcaStatus || '—'} · Test invoice: {check.invoiceNumber || '—'} · {formatDateTime(check.performedAt)}</div>
                        {check.errors.length > 0 &&
                            <ul style={{ color: '#c62828', marginBottom: 0 }}>{check.errors.map((error, i) => <li key={i}>{error}</li>)}</ul>
                        }
                        {check.warnings.length > 0 &&
                            <ul style={{ color: '#8a5a00', marginBottom: 0 }}>{check.warnings.map((warning, i) => <li key={i}>{warning}</li>)}</ul>
                        }
                        {check.message && <p style={{ marginBottom: 0 }}>{check.message}</p>}
                    </div>
                }
            </div>
        );
    }

    render() {
        const access = this.state.access;
        if (access !== undefined && access.viewAccess === false) {
            this.props.history.push('/dashboard');
        }

        const status = this.state.status;
        const unit = status ? status.unit : null;
        const step = status ? status.step : null;
        const problems = (status && status.problems) || [];
        const kinds = (status && status.requiredChecks) || [];
        const canAct = access !== undefined && access.editAccess === true && !this.state.busy && problems.length === 0;
        const checksPassed = kinds.length > 0 && kinds.every(kind => {
            const check = this.latestCheck(kind);
            return check && check.outcome === 'Passed';
        });
        const unitStatus = unit ? (ZATCA_STATUS[unit.onboardingStatus] || ZATCA_STATUS.NotOnboarded) : null;
        const steps = [
            { label: 'Compliance certificate' },
            { label: 'Compliance checks' },
            { label: 'Production certificate' }
        ];

        return (
            <div className="p-grid">
                {this.state.busy && <div className="page-loader"></div>}
                <Toast ref={(el) => this.growl = el} />
                <div className="p-col-12">
                    <div className="card card-w-title" style={{ position: 'relative' }}>
                        <h1>ZATCA Onboarding</h1>

                        {unit &&
                            <div className="p-grid">
                                <div className="p-col-12 p-md-8">
                                    <div><strong>{unit.commonName}</strong> — {unit.branchName}{unit.branchCode ? ' (' + unit.branchCode + ')' : ''}</div>
                                    <div>Environment: {unit.environment} · Invoice types: {invoiceTypeLabel(unit.invoiceTypeFlags)}</div>
                                    <div style={{ wordBreak: 'break-all' }}>EGS serial number: {unit.serialNumber}</div>
                                </div>
                                <div className="p-col-12 p-md-4" style={{ textAlign: 'right' }}>
                                    {unitStatus && <Button type="button" label={unitStatus.label} className={unitStatus.className + ' p-button-rounded'} />}
                                    <Button type="button" label="Refresh" icon="pi pi-refresh" className="ml1 p-button-secondary"
                                        disabled={this.state.busy} onClick={this.loadStatus} />
                                </div>
                                {unit.environment === 'Production' &&
                                    <div className="p-col-12"><div style={warnStyle}>This unit is set to Production. Certificates issued here are real, and invoices from this unit have fiscal effect.</div></div>
                                }
                            </div>
                        }

                        {problems.length > 0 && step !== 'ready' &&
                            <div style={warnStyle}>
                                <strong>Fix these before onboarding:</strong>
                                <ul style={{ marginBottom: 0 }}>{problems.map((problem, i) => <li key={i}>{problem}</li>)}</ul>
                            </div>
                        }

                        {status && <Steps model={steps} activeIndex={STEP_INDEX[step] !== undefined ? STEP_INDEX[step] : 0} readOnly={true} />}
                        {status && status.nextAction && <p style={{ marginTop: '1em' }}>{status.nextAction}</p>}

                        {step === 'request-compliance-csid' &&
                            <div style={sectionStyle}>
                                <h3 style={{ marginTop: 0 }}>1. Compliance certificate</h3>
                                <p>
                                    In the FATOORA portal, choose "Onboard new solution unit/device" and generate an OTP for this unit.
                                    It expires within about an hour and can be used once. It is sent to ZATCA and never stored.
                                </p>
                                <div className="p-grid">
                                    <div className="p-col-12 p-md-4">
                                        <label htmlFor="zatca-onboarding-otp">OTP <span className="requiredColor">*</span></label>
                                        <InputText id="zatca-onboarding-otp" className="p-col-12" value={this.state.otp} keyfilter="pint"
                                            maxLength={10} autoComplete="off" disabled={!canAct}
                                            onChange={(e) => this.setState({ otp: e.target.value })} />
                                    </div>
                                    <div className="p-col-12 p-md-8" style={{ display: 'flex', alignItems: 'flex-end' }}>
                                        {canAct &&
                                            <Button type="button" label="Request compliance certificate" icon="pi pi-key"
                                                disabled={this.state.otp.trim() === ''} onClick={this.requestComplianceCertificate} />
                                        }
                                    </div>
                                </div>
                            </div>
                        }

                        {step === 'run-compliance-checks' &&
                            <div style={sectionStyle}>
                                <h3 style={{ marginTop: 0 }}>2. Compliance checks</h3>
                                <p>
                                    ZATCA checks one sample document for each invoice type this unit issues. Each check issues a real
                                    test invoice on this unit and uses one of its invoice numbers.
                                </p>
                                {kinds.map(kind => this.renderCheck(kind, canAct))}
                                <div style={warnStyle}>
                                    Credit and debit note checks will appear here once the ZATCA service supports those documents.
                                    Outside the sandbox, ZATCA may require them before issuing the production certificate.
                                </div>

                                <h3>3. Production certificate</h3>
                                <p>
                                    {checksPassed
                                        ? 'All checks have passed. Request the production certificate to let this branch issue e-invoices.'
                                        : 'Available once every check above has passed.'}
                                </p>
                                {canAct &&
                                    <Button type="button" label="Request production certificate" icon="pi pi-check"
                                        disabled={!checksPassed} onClick={this.requestProductionCertificate} />
                                }
                            </div>
                        }

                        {step === 'ready' &&
                            <div style={okStyle}>Onboarded. This branch unit can issue ZATCA e-invoices.</div>
                        }

                        {status &&
                            <div style={sectionStyle}>
                                <h3 style={{ marginTop: 0 }}>Certificates</h3>
                                <div className="p-grid">
                                    {this.renderCredential('Compliance certificate', status.complianceCsid)}
                                    {this.renderCredential('Production certificate', status.productionCsid)}
                                </div>
                                {status.productionCsid && status.productionCsid.matchesPrivateKey === false && unit && unit.environment !== 'Sandbox' &&
                                    <div style={warnStyle}>
                                        The production certificate does not match this unit's key, so onboarding did not work. Contact support before issuing invoices.
                                    </div>
                                }
                            </div>
                        }

                        <div className="p-col-12 p-lg-12 mt1">
                            <Link to="/zatca-units"><Button style={{ float: 'right' }} label="Back" icon="pi pi-arrow-left" className="ml1 p-button-secondary" /></Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => {
    return {
        loggedUserDetail: state.auth.userObject
    }
}

export default connect(mapStateToProps)(ZatcaOnboarding);
